import { AlloyLegacyThemeDesignTokens, SliderDesignTokens } from '../types';
import { buildBoxShadow } from './common';
import type { CSSBlock } from '../types/common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

/**
 * Suppresses the thumb's interactive box-shadow (halo) while disabled. Shared
 * between the Alloy and Weave token sets so the reset lives in one place.
 */
const disabledThumbHaloReset: CSSBlock = {
  '&:hover': { 'box-shadow': 'none' },
  '&:focus': { 'box-shadow': 'none' },
  '&:active': { 'box-shadow': 'none' },
};

export const getSliderTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): SliderDesignTokens => ({
  track: {
    _: {
      background: theme.tokens.colors.border.inactive.value,
      'border-radius': '3px',
      height: '1px',
    },
  },
  tick: {
    _: {
      background: theme.tokens.colors.border.inactive.value,
      width: '1px',
      height: '5px',
    },
  },
  trackSpan: {
    _: {
      'border-radius': '3px',
    },
    disabled: {
      true: {
        background: theme.tokens.colors.border.inactive.value,
      },
      false: {
        background: theme.tokens.colors.border.information.value,
      },
    },
  },
  thumb: {
    _: {
      width: '12px',
      height: '12px',
      'border-radius': '50%',
      transition: theme.transitions.create([
        'background',
        'border-color',
        'box-shadow',
      ]),
      '&:focus': {
        'box-shadow': theme.halos.focus,
        border: '0',
        outline: 'none',
      },
      '&:hover': {
        'box-shadow': theme.halos.hover,
      },
      '&:active': {
        'box-shadow': theme.halos.active,
        cursor: 'grabbing',
      },
    },
    disabled: {
      true: {
        background: theme.tokens.colors.background.neutral.inactive.value,
        'pointer-events': 'none',
        'user-select': 'none',
        ...disabledThumbHaloReset,
      },
      false: {
        background: theme.tokens.colors.background.information.value,
      },
    },
  },
});

export const getSliderTokensWeave = (
  tokens: WeaveTokens,
): SliderDesignTokens => {
  const slider = tokens?.component?.slider;
  const thumb = slider?.thumb;
  const opacityDisabled = slider?.opacity?.disabled;

  return {
    track: {
      _: {
        background: slider?.rail?.['background-color'],
        'border-radius': slider?.['border-radius'],
        height: slider?.rail?.height,
      },
      disabled: {
        true: {
          opacity: opacityDisabled,
        },
        false: {},
      },
    },
    tick: {
      _: {
        background: slider?.mark?.['background-color'],
        width: slider?.mark?.width,
        height: slider?.mark?.height,
        'border-radius': slider?.mark?.['border-radius'],
      },
    },
    trackSpan: {
      _: {
        'border-radius': slider?.['border-radius'],
        background: slider?.track?.['background-color'],
      },
    },
    thumb: {
      _: {
        width: thumb?.width,
        height: thumb?.height,
        'border-radius': thumb?.['border-radius'],
        background: thumb?.['background-color'],
        '&:hover': { 'box-shadow': buildBoxShadow(thumb, 'hover') },
        '&:focus': { 'box-shadow': buildBoxShadow(thumb, 'focus') },
        '&:active': { 'box-shadow': buildBoxShadow(thumb, 'pressed') },
      },
      disabled: {
        true: {
          background: thumb?.['background-color'],
          'pointer-events': 'none',
          ...disabledThumbHaloReset,
        },
        false: {},
      },
    },
  };
};
